import React, { type Dispatch, type SetStateAction } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getDefaultCellWidth } from "@features/sales-v3/hooks/use-table-columns";
import { type ColumnConfig } from "@features/sales-v3/hooks/use-table-state";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { Switch } from "@shared/switch";

import { PanelContainer } from "./panel-container";

type Props = {
  availableMetricIds: Set<string>;
  metricIdsWithoutMiniGraph: Set<string>;
  selectedColumnConfigs: ColumnConfig[];
  setSelectedColumnConfigs: Dispatch<SetStateAction<ColumnConfig[]>>;
  metricsById: { [id: string]: SalesPageMetric };
};

export const ColumnOrderPanel = ({
  metricsById,
  availableMetricIds,
  metricIdsWithoutMiniGraph,
  selectedColumnConfigs,
  setSelectedColumnConfigs,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = selectedColumnConfigs.findIndex(
        ({ id }) => id === active.id,
      );
      const newIndex = selectedColumnConfigs.findIndex(
        ({ id }) => id === over?.id,
      );

      const newOrdering = arrayMove(selectedColumnConfigs, oldIndex, newIndex);

      prettyLogEventPage({
        domain: "Customize Table Modal",
        action: "Other",
        additionalActionInfo: "Reorder Column Configs",
        data: {
          newSelectedColumnConfigs: newOrdering,
          pageContext: { selectedColumnConfigs },
        },
      });

      setSelectedColumnConfigs(newOrdering);
    }
  };

  const visibleColumnConfigsCount = selectedColumnConfigs.filter(({ id }) =>
    availableMetricIds.has(id),
  ).length;

  const deleteAll = () => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Delete All Metrics",
    });

    setSelectedColumnConfigs((prevSelectedColumnConfigs) =>
      prevSelectedColumnConfigs.filter(({ id }) => {
        if (availableMetricIds.has(id)) {
          return false;
        }
        return true;
      }),
    );
  };

  const toggleMiniGraph = (columnConfig: ColumnConfig) => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Toggle Mini Graph",
      data: {
        pageContext: { columnConfig },
      },
    });

    setSelectedColumnConfigs((prev) =>
      prev.map((c) => {
        const EXTRA_GRAPH_WIDTH = 40;
        const showMiniGraph = !c.showMiniGraph;
        const width = getDefaultCellWidth(metricsById[c.id]?.name ?? "");
        const isDefaultWidth =
          c.width === width || c.width === width + EXTRA_GRAPH_WIDTH;

        let newWidth = c.width;
        if (showMiniGraph && Number(c.width) <= width + EXTRA_GRAPH_WIDTH) {
          newWidth = width + EXTRA_GRAPH_WIDTH;
        } else if (isDefaultWidth) {
          newWidth = width;
        }

        return c.id === columnConfig.id
          ? {
              ...c,
              showMiniGraph,
              width: newWidth,
            }
          : c;
      }),
    );
  };

  const removeColumn = (columnConfig: ColumnConfig) => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Remove Metric",
      data: {
        pageContext: { columnConfig },
      },
    });

    setSelectedColumnConfigs((prev) =>
      prev.filter((c) => c.id !== columnConfig.id),
    );
  };

  return (
    <PanelContainer title="COLUMN ORDER">
      <div className="px-4 h-full">
        <div className="text-xs">
          <span>({visibleColumnConfigsCount}) Selected metrics</span>
          {visibleColumnConfigsCount > 0 && (
            <span
              className="text-mars underline ml-2 cursor-pointer"
              onClick={deleteAll}
            >
              Delete all
            </span>
          )}
        </div>
        <div className="overflow-hidden h-[calc(100%-36px)]">
          <div
            className={clsx(
              "h-full overflow-x-hidden overflow-y-auto py-3 grid gap-y-2",
              "gap-x-0.5 grid-cols-[calc(100%-32px)_32px] auto-rows-min",
            )}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext items={selectedColumnConfigs}>
                {selectedColumnConfigs.map((columnConfig) => {
                  if (!availableMetricIds.has(columnConfig.id)) {
                    return null;
                  }

                  return (
                    <ColumnOrderRow
                      key={columnConfig.id}
                      columnConfig={columnConfig}
                      showMiniGraphOption={
                        !metricIdsWithoutMiniGraph.has(columnConfig.id)
                      }
                      toggleMiniGraph={toggleMiniGraph}
                      removeColumn={removeColumn}
                      metricsById={metricsById}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </PanelContainer>
  );
};

const ColumnOrderRow = ({
  columnConfig,
  showMiniGraphOption,
  toggleMiniGraph,
  removeColumn,
  metricsById,
}: {
  columnConfig: ColumnConfig;
  showMiniGraphOption: boolean;
  toggleMiniGraph: (columnConfig: ColumnConfig) => void;
  removeColumn: (columnConfig: ColumnConfig) => void;
  metricsById: { [id: string]: SalesPageMetric };
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: columnConfig.id,
    });

  const spec = metricsById[columnConfig.id];
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="text-sm px-3 py-1.5 border rounded-md bg-gray-4 flex items-center"
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
      >
        <i
          className="fa-regular fa-solid fa-grip-dots text-cosmic mr-2.5"
          ref={setNodeRef}
          {...attributes}
          {...listeners}
        />
        <GenericTooltip
          className="truncate flex-grow"
          overlayClassName="pointer-events-none"
          noInfoCircle
          mouseEnterDelay={300}
          content={
            <div className="text-xs max-w-[360px]">
              <div className="text-gray+1 font-light mb-0.5">Metric Name</div>
              <p>{spec.name}</p>
              <div className="text-gray+1 font-light mb-0.5">What is it?</div>
              <p dangerouslySetInnerHTML={{ __html: spec.descriptionHTML }}></p>
            </div>
          }
        >
          {spec.name}
        </GenericTooltip>
        {showMiniGraphOption && (
          <>
            <i className="fa-regular fa-solid fa-line-chart text-gray+1 mx-2" />
            <GenericTooltip
              overlayClassName="pointer-events-none"
              noInfoCircle
              mouseEnterDelay={100}
              content="Add mini-graph"
            >
              <Switch
                id={columnConfig.id}
                size="small"
                value={columnConfig.showMiniGraph}
                onChange={() => toggleMiniGraph(columnConfig)}
              />
            </GenericTooltip>
          </>
        )}
      </motion.div>
      <div
        className={clsx(
          "cursor-pointer justify-self-center self-center rounded-full",
          "hover:bg-mars-2 py-1 px-2",
        )}
        onClick={() => removeColumn(columnConfig)}
      >
        <i className="fa-regular fa-sharp fa-trash text-mars" />
      </div>
    </>
  );
};
